import { ActiveSelection, Canvas, Circle, Group, IEvent, Object, Rect, Textbox } from 'fabric/fabric-impl';

import { round } from 'shared/utils/round';

export default class SnapToGrid {
  static gridStep = 10;
  static isSnapping = false;

  static roundByGrid(num: number) {
    const round = Math.round(num / SnapToGrid.gridStep) * SnapToGrid.gridStep;

    if (round <= 0) {
      return Math.ceil(num / SnapToGrid.gridStep) * SnapToGrid.gridStep;
    } else {
      return round;
    }
  }

  static onFabricObjectScaling(canvas: Canvas, e: IEvent<Event>) {
    if (!e.transform) return;
    const active = canvas.getActiveObject();
    if (!active) {
      return;
    }
    if (active.top === undefined || active.left === undefined) {
      return;
    }
    if (active.width === undefined || active.height === undefined) {
      return;
    }
    if (active.scaleY === undefined || active.scaleX === undefined) {
      return;
    }

    const strokeWidth = active.strokeWidth ?? 1;
    const [width, height] = [active.getScaledWidth(), active.getScaledHeight()];

    // X
    if (['tl', 'ml', 'bl'].indexOf(e.transform.corner) !== -1) {
      const tl = this.roundByGrid(active.left);
      active.scaleX = (width + active.left - tl) / (active.width + strokeWidth);
      active.left = tl;
    } else if (['tr', 'mr', 'br'].indexOf(e.transform.corner) !== -1) {
      const tl = this.roundByGrid(active.left + width);
      active.scaleX = (tl - active.left) / (active.width + strokeWidth);
    }

    // Y
    if (['tl', 'mt', 'tr'].indexOf(e.transform.corner) !== -1) {
      const tt = this.roundByGrid(active.top);
      active.scaleY = (height + active.top - tt) / (active.height + strokeWidth);
      active.top = tt;
    } else if (['bl', 'mb', 'br'].indexOf(e.transform.corner) !== -1) {
      const tt = this.roundByGrid(active.top + height);
      active.scaleY = (tt - active.top) / (active.height + strokeWidth);
    }

    // Avoid singularities
    active.scaleX = (active.scaleY >= 0 ? 1 : -1) * Math.max(Math.abs(active.scaleX), 0.001);
    active.scaleY = (active.scaleY >= 0 ? 1 : -1) * Math.max(Math.abs(active.scaleY), 0.001);

    this.transformAfterScaling(active);
    canvas?.renderAll();
  }

  static transformAfterScaling(active: Object, parentScaling?: { scaleX: number; scaleY: number }) {
    const isActiveSelection = active?.type === 'activeSelection';
    const isGroupedObjects = active?.data?.type === 'group';
    if (isActiveSelection || isGroupedObjects) {
      const objects = (active as ActiveSelection).getObjects();
      for (const obj of objects) {
        this.transformAfterScaling(obj, active.getObjectScaling());
      }
      return;
    }

    const isRect = active?.type === 'rect';
    if (isRect) this.snapReact(active as Rect, parentScaling);

    let isGroup = active?.type === 'group';
    if (isGroup) this.snapGroup(active as Group, parentScaling);
  }

  static onFabricObjectMoving(canvas: Canvas, event: IEvent<Event>) {
    const coord = event.target?.getPointByOrigin('left', 'top');
    if (!coord) return;
    event.target?.set({ left: SnapToGrid.roundByGrid(coord.x), top: SnapToGrid.roundByGrid(coord.y) });
    canvas.renderAll();
  }

  static setGridStep(step: number) {
    this.gridStep = step;
  }

  static enableSnapToGrid(canvas: Canvas) {
    canvas.on('object:scaling', this.onFabricObjectScaling.bind(this, canvas));
    canvas.on('object:moving', this.onFabricObjectMoving.bind(this, canvas));
    this.isSnapping = true;
  }

  static disableSnapToGrid(canvas: Canvas) {
    canvas.off('object:scaling', this.onFabricObjectScaling.bind(this, canvas));
    canvas.off('object:moving', this.onFabricObjectMoving.bind(this, canvas));
    this.isSnapping = false;
  }

  /**
   * Метод для обработки масштабирования индикаторов teamPeriodFouls
   * @param active Активная группа с индикаторами
   * @param parentScaling Масштаб родительского элемента
   */
  static handleTeamPeriodFoulsScaling(active: Group, parentScaling?: { scaleX: number; scaleY: number }) {
    if (active.width === undefined || active.height === undefined) return;

    // Получаем масштабы группы
    const scaleX = parentScaling?.scaleX ? parentScaling.scaleX * (active.scaleX ?? 1) : (active.scaleX ?? 1);
    const scaleY = parentScaling?.scaleY ? parentScaling.scaleY * (active.scaleY ?? 1) : (active.scaleY ?? 1);

    // Получаем фон (первый прямоугольник в группе)
    const background = active.getObjects('rect')[0];
    if (!background) return;

    // Получаем все индикаторы (круги или квадраты, кроме фона)
    const indicators = active.getObjects().filter((obj) => (obj.type === 'circle' || obj.type === 'rect') && obj !== background);

    if (indicators.length === 0) return;

    // Определяем параметры из данных объекта
    const maxCount = active.data?.maxCount ?? 5;
    const isHorizontal = active.data?.orientation === 'horizontal';
    const spacing = active.data?.spacing ?? 10;
    const isCircle = active.data?.shape === 'circle';

    // Базовые размеры группы (немасштабированные) с округлением близких к целым значений
    const groupWidth = this.roundIfCloseToInteger(active.getScaledWidth());
    const groupHeight = this.roundIfCloseToInteger(active.getScaledHeight());

    const workAreaWidth = this.roundIfCloseToInteger(groupWidth);
    const workAreaHeight = this.roundIfCloseToInteger(groupHeight);

    const length = isHorizontal ? workAreaWidth : workAreaHeight;
    const perpendicular = isHorizontal ? workAreaHeight : workAreaWidth;

    if (isCircle) {
      let radius = 0;

      const isPerpendicularLimit = perpendicular > 0;
      const isPlacementLimit = length - (maxCount - 1) * spacing > 0;
      if (isPerpendicularLimit && isPlacementLimit) {
        const rHeight = perpendicular / 2;
        const rWidth = (length - (maxCount - 1) * spacing) / (2 * maxCount);
        radius = Math.min(rHeight, rWidth);
      }

      for (let i = 0; i < maxCount; i++) {
        const offset = i * (2 * radius + spacing);
        const startPos = -((maxCount - 1) * (2 * radius + spacing)) / 2;
        const pos = startPos + offset;
        const posX = isHorizontal ? pos / scaleX : 0;
        const posY = isHorizontal ? 0 : pos / scaleY;
        const indicator = indicators[i];
        (indicator as Circle).set({
          left: posX,
          top: posY,
          radius: radius,
          originX: 'center',
          originY: 'center',
          scaleX: 1 / scaleX,
          scaleY: 1 / scaleY,
          strokeWidth: 0,
        });
      }
    } else {
      const calcSide = (length - (maxCount - 1) * spacing) / maxCount;
      let width = Math.max(isHorizontal ? calcSide : perpendicular, 0);
      let height = Math.max(isHorizontal ? perpendicular : calcSide, 0);

      for (let i = 0; i < maxCount; i++) {
        const side = isHorizontal ? width : height;
        const offset = i * (side + spacing);
        const startPos = -((maxCount - 1) * (side + spacing)) / 2;
        const pos = startPos + offset;
        const posX = isHorizontal ? pos / scaleX : 0;
        const posY = isHorizontal ? 0 : pos / scaleY;
        const indicator = indicators[i];
        (indicator as Rect).set({
          left: posX,
          top: posY,
          width: width,
          height: height,
          originX: 'center',
          originY: 'center',
          scaleX: 1 / scaleX,
          scaleY: 1 / scaleY,
          strokeWidth: 0,
        });
      }
    }
  }

  static snapToGrid(objects: Object[]) {
    for (const object of objects) {
      const left = round(object.left ?? 0);
      const top = round(object.top ?? 0);
      object.set({
        left: this.roundByGrid(left),
        top: this.roundByGrid(top),
      });

      if (object?.data?.type === 'text') {
        const width = round(object.width ?? 0);
        const height = round(object.height ?? 0);
        object.set({
          width: this.roundByGrid(width),
          height: this.roundByGrid(height),
        });
      } else {
        if (object.scaleX === undefined || object.scaleY === undefined) {
          return;
        }
        if (object.width === undefined || object.height === undefined) {
          return;
        }
        if (object.left === undefined || object.top === undefined) {
          return;
        }

        const [scaledWidth, scaledHeight] = [object.getScaledWidth(), object.getScaledHeight()];
        const strokeWidth = object.strokeWidth ?? 1;

        const tl = this.roundByGrid(object.left + scaledWidth);
        object.scaleX = (tl - object.left) / (object.width + strokeWidth);

        const tt = this.roundByGrid(object.top + scaledHeight);
        object.scaleY = (tt - object.top) / (object.height + strokeWidth);

        object.scaleX = (object.scaleY >= 0 ? 1 : -1) * Math.max(Math.abs(object.scaleX), 0.001);
        object.scaleY = (object.scaleY >= 0 ? 1 : -1) * Math.max(Math.abs(object.scaleY), 0.001);
      }

      this.transformAfterScaling(object);
    }
  }

  private static snapReact(active: Rect, parentScaling?: { scaleX: number; scaleY: number }) {
    const rx = active.data?.rx;
    const ry = active.data?.ry;
    if (!rx || !ry) return;
    const scaleX = parentScaling?.scaleX ? parentScaling?.scaleX * (active.scaleX ?? 1) : active.scaleX;
    const scaleY = parentScaling?.scaleY ? parentScaling?.scaleY * (active.scaleY ?? 1) : active.scaleY;
    if (!scaleX || !scaleY) return;
    active.set({ rx: rx * (1 / scaleX), ry: ry * (1 / scaleY) });
  }

  private static snapGroup(active: Group, parentScaling?: { scaleX: number; scaleY: number }) {
    const type = active.data?.type;
    if (!type) return;
    if (type === 'image') {
      this.snapImagePlaceHolder(active, parentScaling);
    } else if (type === 'text') {
      this.snapTextPlaceHolder(active, parentScaling);
    } else if (type === 'composition') {
      this.snapCompositionPlaceHolder(active, parentScaling);
    }
  }

  private static snapImagePlaceHolder(active: Group, parentScaling?: { scaleX: number; scaleY: number }) {
    if (active.top === undefined || active.left === undefined) {
      return;
    }
    if (active.width === undefined || active.height === undefined) {
      return;
    }

    const scaleX = parentScaling?.scaleX ? parentScaling?.scaleX * (active.scaleX ?? 1) : active.scaleX;
    const scaleY = parentScaling?.scaleY ? parentScaling?.scaleY * (active.scaleY ?? 1) : active.scaleY;
    if (scaleY === undefined || scaleX === undefined) {
      return;
    }

    const objects = active.getObjects();
    if (objects.length === 0) return;
    const text = objects[1];
    text.set({ scaleX: 1 / scaleX, scaleY: 1 / scaleY });
    const defaultScaleX = 1 / scaleX;
    const defaultScaleY = 1 / scaleY;

    if (!text.scaleX || !text.scaleY) return;
    if (!text.width || !text.height) return;
    if (text.width > active.width * scaleX) {
      if (text.width < active.width * scaleX) {
        return;
      }
      const factorScaleX = (active.width * scaleX) / text.width;
      const factorScaleY = (active.height * scaleY) / text.height;
      const factorScale = Math.min(factorScaleX, factorScaleY);
      text.set({ scaleX: defaultScaleX * factorScale, scaleY: defaultScaleY * factorScale });
    } else {
      if (text.height < active.height * scaleY) return;
      const factorScale = (active.height * scaleY) / text.height;
      text.set({ scaleX: defaultScaleX * factorScale, scaleY: defaultScaleY * factorScale });
    }
  }

  private static snapTextPlaceHolder(group: Group, parentScaling?: { scaleX: number; scaleY: number }) {
    const scaleX = parentScaling?.scaleX ? parentScaling?.scaleX * (group.scaleX ?? 1) : group.scaleX;
    const scaleY = parentScaling?.scaleY ? parentScaling?.scaleY * (group.scaleY ?? 1) : group.scaleY;
    if (
      group.top === undefined ||
      group.left === undefined ||
      group.width === undefined ||
      group.height === undefined ||
      scaleY === undefined ||
      scaleX === undefined
    ) {
      return;
    }

    const objects = group.getObjects();
    if (objects.length === 0) return;
    const text = objects[0] as Textbox;
    if (!text) return;
    const defaultScaleX = 1 / scaleX;
    const defaultScaleY = 1 / scaleY;
    text.set({ scaleX: defaultScaleX, scaleY: defaultScaleY });
    if (!text.width || !text.height) return;
    const fontSize = (group.data?.fontSize ?? 20) / defaultScaleY;
    text.set({ width: group.width * scaleX * 0.98, fontSize });
    // text.set({ width: group.width * scaleX, fontSize })
    const textboxWidth = text.width;

    if (textboxWidth > group.width * scaleX || text.height > group.height * scaleY) {
      const factorScaleX = (group.width * scaleX) / textboxWidth;
      const factorScaleY = (group.height * scaleY) / text.height;
      const factorScale = Math.min(factorScaleX, factorScaleY);
      text.set({ scaleX: defaultScaleX * factorScale * 0.95, scaleY: defaultScaleY * factorScale });
      // text.set({ scaleX: defaultScaleX * factorScale, scaleY: defaultScaleY * factorScale })
    }
  }

  private static snapCompositionPlaceHolder(
    active: Group,
    parentScaling:
      | {
          scaleX: number;
          scaleY: number;
        }
      | undefined,
  ) {
    switch (active.data?.indicator) {
      case 'teamPeriodFouls':
        this.handleTeamPeriodFoulsScaling(active, parentScaling);
        break;
      default:
        break;
    }
  }

  // Внутренняя функция для округления значений, близких к целым числам
  private static roundIfCloseToInteger(value: number): number {
    // Округляем только если значение отличается от целого на очень малую величину
    // Например, 119.99999999999994 -> 120, но 199.85 -> 199.85
    const epsilon = 0.0001; // Погрешность для определения "близости" к целому числу
    const rounded = Math.round(value);
    if (Math.abs(value - rounded) < epsilon) {
      return rounded;
    }
    return value;
  }
}
